import { listSermons } from "@/api/sermons";
import { useResource } from "@/hooks/useResource";
import { formatLongDate } from "@/lib/dates";
import { thumbnailFor } from "@/lib/media";
import { Button } from "@/components/ui/Button";
import { Async } from "@/components/ui/Async";
import { Container, Eyebrow, SectionHeading, Skeleton } from "@/components/ui/Primitives";
import { Reveal } from "@/components/motion/Reveal";
import { MediaPlayer } from "@/components/media/MediaPlayer";
import { AudioPlayer } from "@/components/media/AudioPlayer";
import { MediaArt, SermonCard } from "@/components/cards/Cards";
import { Link } from "@/router/Router";

/** Latest message + a rail of recent sermons, streaming-platform style. */
export function LatestMessage() {
  const sermons = useResource("sermons:50", (signal) => listSermons(50, signal));

  return (
    <section className="latest" aria-labelledby="latest-title">
      <Container>
        <SectionHeading
          eyebrow="Watch"
          index="03"
          id="latest-title"
          title={
            <>
              The latest <em>message</em>
            </>
          }
          action={
            <Button to="/watch/sermons" variant="ghost" iconRight="arrowRight">
              Message library
            </Button>
          }
        />
        <Async
          resource={sermons}
          skeleton={
            <div className="latest__feature" aria-hidden>
              <Skeleton className="skeleton--video" />
              <div>
                <Skeleton className="skeleton--line skeleton--short" />
                <Skeleton className="skeleton--title" />
                <Skeleton className="skeleton--line" />
              </div>
            </div>
          }
          empty={{ icon: "video", title: "New messages are on their way", text: "Sermons will appear here as soon as they're published.", action: <Button to="/watch/live" variant="ghost" size="sm">Watch live</Button> }}
          errorTitle="Messages didn't load"
        >
          {(list) => {
            const [latest, ...rest] = list;
            return (
              <>
                <Reveal className="latest__feature">
                  <div className="latest__player">
                    {latest.videoUrl ? (
                      <MediaPlayer url={latest.videoUrl} type="video" title={latest.title} poster={latest.thumbnail ?? thumbnailFor(latest.videoUrl)} />
                    ) : (
                      <Link to={`/watch/sermons/${encodeURIComponent(latest.id)}`} className="media-frame latest__art">
                        <MediaArt title={latest.title} />
                      </Link>
                    )}
                  </div>
                  <div className="latest__info">
                    <Eyebrow tone="leaf">Latest message</Eyebrow>
                    <h3 className="latest__title">
                      <Link to={`/watch/sermons/${encodeURIComponent(latest.id)}`}>{latest.title}</Link>
                    </h3>
                    <p className="latest__meta">{[latest.preacher, formatLongDate(latest.date)].filter(Boolean).join(" · ")}</p>
                    {latest.description ? <p className="latest__desc">{latest.description}</p> : null}
                    {latest.audioUrl ? (
                      <div className="latest__audio">
                        <p className="latest__audio-label">Prefer to listen?</p>
                        <AudioPlayer src={latest.audioUrl} title={latest.title} subtitle={latest.preacher ?? undefined} variant="compact" bars={40} />
                      </div>
                    ) : null}
                  </div>
                </Reveal>
                {rest.length ? (
                  <div className="rail" role="region" aria-label="More recent messages" tabIndex={0}>
                    {rest.slice(0, 8).map((s) => (
                      <SermonCard key={s.id} sermon={s} />
                    ))}
                  </div>
                ) : null}
              </>
            );
          }}
        </Async>
      </Container>
    </section>
  );
}
